// cue 配列を「文(sentence)」単位にまとめる（ディクテーション用）。
// デスクトップ版 src/main.js の segmentSentences 相当を PWA(ブラウザ) に移植したもの。
//   - cue のテキストを空白連結 → 文区切り → 各文の start/end を「連結テキスト上の文字位置」を
//     含む cue 内で文字割合で按分して復元（1 cue を複数文が共有しても時刻が重なりにくい）。
//   - 文区切りは英文向けの軽量ルール（. ! ? の後に空白＋大文字/引用符/数字…で切る。
//     よくある略語 Mr. Mrs. Dr. etc. では切らない）。デスクトップは sbd を使うが PWA は
//     依存を増やさず自前の簡易 SBD で代替する（精度はわずかに落ちるが採点単位として実用十分）。
// 戻り値: [{ text, start, end }]（start 昇順）。

const ABBREV = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'mt', 'vs', 'etc', 'inc',
  'ltd', 'co', 'dept', 'no', 'vol', 'fig', 'gen', 'gov', 'sen', 'rep', 'col',
  'capt', 'lt', 'sgt', 'cmdr', 'rev', 'hon', 'esp', 'approx', 'apt', 'dept',
  'a.m', 'p.m', 'i.e', 'e.g', 'u.s', 'u.k', 'ph.d',
])

// 連結テキストを文に分割。各要素は trim 済みの文字列。
function splitSentences(text) {
  const s = String(text || '')
  if (!s.trim()) return []
  const out = []
  let start = 0
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (ch !== '.' && ch !== '!' && ch !== '?') continue
    // 連続する終端記号（!? や …）はまとめて1境界に。
    let j = i
    while (j + 1 < s.length && (s[j + 1] === '.' || s[j + 1] === '!' || s[j + 1] === '?')) j++
    // 終端記号の直後に閉じ引用符/括弧が来ることがある（"…end." → end."）。
    let k = j
    while (k + 1 < s.length && /["')\]”’]/.test(s[k + 1])) k++
    const after = s[k + 1]
    // 文末判定: 末尾 or 直後が空白で、その先が文頭らしい（大文字/引用符/数字/開き括弧）。
    if (after === undefined) {
      out.push(s.slice(start, k + 1).trim())
      start = k + 1
      i = k
      continue
    }
    if (!/\s/.test(after)) { i = j; continue }
    // 略語チェック（ピリオドが1個のときだけ）。直前の語が既知の略語なら切らない。
    if (i === j && ch === '.') {
      const before = s.slice(Math.max(0, start), i)
      const mword = before.match(/([A-Za-z][A-Za-z.]*)$/)
      if (mword && ABBREV.has(mword[1].toLowerCase().replace(/\.$/, ''))) { i = j; continue }
      // 1文字の頭字語（"A." など）も切らない（イニシャル）。
      if (mword && mword[1].replace(/\./g, '').length <= 1) { i = j; continue }
    }
    // 直後の最初の非空白が文頭らしいか。
    const m = s.slice(k + 1).match(/\S/)
    const nextCh = m ? m[0] : ''
    if (nextCh && !/[A-Z0-9"'(\[“‘]/.test(nextCh)) { i = j; continue }
    out.push(s.slice(start, k + 1).trim())
    start = k + 1
    i = k
  }
  const tail = s.slice(start).trim()
  if (tail) out.push(tail)
  return out.filter(Boolean)
}

export function segmentSentences(cues) {
  if (!Array.isArray(cues) || !cues.length) return []
  let combined = ''
  const spans = [] // 各 cue の連結テキスト上の範囲＋時刻
  for (const c of cues) {
    const txt = String((c && c.text) || '').trim()
    if (!txt) continue
    if (combined) combined += ' '
    const cs = combined.length
    combined += txt
    spans.push({ cs, ce: combined.length, start: c.start, end: c.end })
  }
  if (!combined || !spans.length) return []

  let sents
  try {
    sents = splitSentences(combined)
  } catch {
    sents = [combined]
  }
  if (!sents.length) sents = [combined]

  // 文字位置 pos を含む cue 内で、時刻を文字割合で按分。
  const timeAt = (pos) => {
    const sp = spans.find((x) => pos < x.ce) || spans[spans.length - 1]
    const span = Math.max(1, sp.ce - sp.cs)
    const frac = Math.min(1, Math.max(0, (pos - sp.cs) / span))
    return sp.start + frac * (sp.end - sp.start)
  }

  const out = []
  let cursor = 0
  for (const sRaw of sents) {
    const st = sRaw.trim()
    if (!st) continue
    let from = combined.indexOf(st, cursor)
    if (from < 0) from = combined.indexOf(st)
    let to
    if (from < 0) {
      from = cursor
      to = Math.min(combined.length, cursor + st.length)
    } else {
      to = from + st.length
      cursor = to
    }
    out.push({ text: st, start: timeAt(from), end: timeAt(Math.max(from, to - 1)) })
  }
  // start 昇順を保証（按分で前後しないが念のため）。
  out.sort((a, b) => a.start - b.start)
  return out
}
